import { useEffect, useState, useRef } from 'react';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { APP_CONFIG } from '../config/menu.config';
import { TopHeader } from '../components/TopHeader';
import { BreadcrumbBar } from '../components/BreadcrumbBar';
import { LeftPanel } from '../components/LeftPanel';
import { RightSidebar } from '../components/RightSidebar';
import { StatusBar } from '../components/StatusBar';
import { CenterMenu } from '../components/CenterMenu';
import { ListModal } from '../components/ListModal';
import { FullScreenListView } from '../views/FullScreenListView';
import { VoucherEngineWrapper } from '../views/VoucherEngineWrapper';
import { DayBookView } from '../views/DayBookView';
import { BalanceSheetView } from '../views/BalanceSheetView';
import { ProfitLossView } from '../views/ProfitLossView';
import { Gstr1View } from '../views/Gstr1View';
import { Gstr3bView } from '../views/Gstr3bView';
import { GstSummaryView } from '../views/GstSummaryView';
import { PartyRegisterView } from '../views/PartyRegisterView';
import { KeyboardHelpOverlay } from '../components/KeyboardHelpOverlay';
import { LedgerVouchersView } from '../views/LedgerVouchersView';
import { GroupSummaryView } from '../views/GroupSummaryView';
import { LedgerCreationView } from '../views/LedgerCreationView';
import { GroupCreationView } from '../views/GroupCreationView';
import { TrialBalanceView } from '../views/TrialBalanceView';
import { CashFlowView } from '../views/CashFlowView';
import { FundsFlowView } from '../views/FundsFlowView';
import { RatioAnalysisView } from '../views/RatioAnalysisView';
import { BankReconciliationView } from '../views/BankReconciliationView';
import { StockSummaryView } from '../views/StockSummaryView';
import { StockItemVouchersView } from '../views/StockItemVouchersView';
import { StockGroupCreationView } from '../views/StockGroupCreationView';
import { StockCategoryCreationView } from '../views/StockCategoryCreationView';
import { StockItemCreationView } from '../views/StockItemCreationView';
import { UnitCreationView } from '../views/UnitCreationView';
import { LocationCreationView } from '../views/LocationCreationView';
import { CompanyCreationView } from '../views/CompanyCreationView';
import { SelectCompanyView } from '../views/SelectCompanyView';
import { CompanyDeleteConfirmView } from '../views/CompanyDeleteConfirmView';
import { CompanyBackupView } from '../views/CompanyBackupView';
import { CustomizationView } from '../views/CustomizationView';
import { CompanyRestoreView } from '../views/CompanyRestoreView';
import { LicensePanelModal } from '../components/LicensePanelModal';
import { RegisterSummaryView } from '../views/RegisterSummaryView';
import { VoucherRegisterView } from '../views/VoucherRegisterView';
import { BinBoxView } from '../views/BinBoxView';
import { EmployeeCreationView } from '../views/EmployeeCreationView';
import { EmployeeListView } from '../views/EmployeeListView';
import { LeaveManagementView } from '../views/LeaveManagementView';
import { LoanManagementView } from '../views/LoanManagementView';
import { AttendanceManagementView } from '../views/AttendanceManagementView';
import { PayrollProcessingView } from '../views/PayrollProcessingView';
import { PayrollSummaryView } from '../views/PayrollSummaryView';
import { PayslipView } from '../views/PayslipView';
import { SalaryComponentListView } from '../views/SalaryComponentListView';
import { SalaryComponentCreationView } from '../views/SalaryComponentCreationView';
import { SalaryStructureView } from '../views/SalaryStructureView';
import { SalaryStructureEditView } from '../views/SalaryStructureEditView';
import { DashboardView } from '../views/DashboardView';
import { CurrencyCreationView } from '../views/CurrencyCreationView';
import { VoucherTypeCreationView } from '../views/VoucherTypeCreationView';
import { SimpleReportView } from '../views/SimpleReportView';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { PrintManager } from '../components/PrintManager';
import { DateInput } from '../components/DateInput';
import BankImport from '../views/BankImportView';
import InvoiceImport from '../views/InvoiceImportView';

import { QuickAccessSearch } from '../components/QuickAccessSearch';
import { F10CenterView } from '../views/F10CenterView';
import { QuotationView, QuotationListView } from '../views/QuotationView';
import { ComingSoonView } from '../views/ComingSoonView';

export const DashboardLayout = () => {
  const { state, derived, actions } = useAppNavigation(APP_CONFIG);
  const fromDate = useStore(s => s.fromDate);
  const setFromDate = useStore(s => s.setFromDate);
  const toDate = useStore(s => s.toDate);
  const setToDate = useStore(s => s.setToDate);
  const printRequested = useStore(s => s.printRequested);
  
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const toDateInputRef = useRef<HTMLInputElement>(null);

  const applyPeriod = () => {
    if (tempFromDate && tempToDate) {
      setFromDate(tempFromDate);
      setToDate(tempToDate);
    }
    setShowPeriodModal(false);
  };

  useEffect(() => {
    const handleClickOutside = () => actions.setOpenMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [actions]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && e.altKey) {
        const isLocked = !useStore.getState().activeCompany;
        if (isLocked) {
           e.preventDefault();
           return;
        }
        e.preventDefault();
        setTempFromDate(fromDate);
        setTempToDate(toDate);
        setShowPeriodModal(true);
      } else if (e.key === 'F10') {
        const isLocked = !useStore.getState().activeCompany;
        // Only open F10 Center from gateway — not inside vouchers/forms
        const isInVoucher = document.querySelector('[data-voucher-engine]') !== null;
        if (!isLocked && !isInVoucher) {
          e.preventDefault();
          actions.handleItemSelect({ name: 'F10 Center', level: 'f10_center' });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fromDate, toDate, actions]);

  useEffect(() => {
    if (state.masterSearch && !state.isFullScreenList && !state.isDashboardView && !state.isVoucherView && !state.isDayBookView && !state.isBalanceSheetView && !state.isProfitLossView && !state.isLedgerVouchersView && !state.isGroupSummaryView && !state.isLedgerCreationView && !state.isGroupCreationView && !state.isStockSummaryView && !state.isStockItemVouchersView && !state.isStockGroupCreationView && !state.isStockCategoryCreationView && !state.isStockItemCreationView && !state.isUnitCreationView && !state.isLocationCreationView && !state.isEmployeeCreationView && !state.isEmployeeListView && !state.isPayrollProcessingView && !state.isPayrollSummaryView && !state.isPayslipView && state.activeMenuLevel !== 'payroll_leave' && state.activeMenuLevel !== 'payroll_loans') {
      const lowerVal = state.masterSearch.toLowerCase();
      let idx = derived.flatItems.findIndex((i: any) => i.name?.toLowerCase().startsWith(lowerVal));
      if (idx === -1) idx = derived.flatItems.findIndex((i: any) => i.name?.toLowerCase().includes(lowerVal));
      if (idx !== -1) actions.setSelectedIndex(idx);
    }
  }, [state.masterSearch, derived.flatItems, state.isFullScreenList, state.isVoucherView, state.isDayBookView, state.isBalanceSheetView, state.isProfitLossView, state.isLedgerVouchersView, state.isGroupSummaryView, state.isLedgerCreationView, state.isGroupCreationView, state.isEmployeeCreationView, state.isEmployeeListView, state.isPayrollProcessingView, state.isPayrollSummaryView, state.isPayslipView, state.activeMenuLevel, actions]);

  const hideSidebars = state.isDashboardView || state.isVoucherView || state.isDayBookView || state.isBinBoxView || state.activeMenuLevel === 'f10_center' || state.isBalanceSheetView || state.isProfitLossView || state.isLedgerVouchersView || state.isGroupSummaryView || state.isLedgerCreationView || state.isGroupCreationView || state.isStockSummaryView || state.isStockItemVouchersView || state.isStockGroupCreationView || state.isStockCategoryCreationView || state.isStockItemCreationView || state.isUnitCreationView || state.isLocationCreationView || state.isCompanyCreationView || state.isSelectCompanyView || state.activeMenuLevel === 'delete_company_confirm' || state.activeMenuLevel === 'backup_company_process' || state.activeMenuLevel === 'restore_company' || state.activeMenuLevel === 'customization' || state.isRegisterSummaryView || state.isVoucherRegisterView || state.isEmployeeCreationView || state.isEmployeeListView || state.isPayrollProcessingView || state.isPayrollSummaryView || state.isPayslipView || state.isSalaryComponentsView || state.isSalaryStructureView || state.activeMenuLevel === 'payroll_leave' || state.activeMenuLevel === 'payroll_loans' || state.activeMenuLevel === 'payroll_attendance' || state.isQuotationCreationView || state.isQuotationAlterView || state.activeMenuLevel === 'import_bank' || state.activeMenuLevel === 'import_invoice' || state.activeMenuLevel === 'gstr1' || state.activeMenuLevel === 'gstr3b' || state.activeMenuLevel === 'gst_summary' || state.activeMenuLevel === 'party_register' || state.activeMenuLevel === 'display_groupsummary' || state.activeMenuLevel === 'display_groupvouchers' || ['display_outstanding', 'display_statistics', 'display_stockmovement', 'display_stockquery', 'display_costestimation', 'display_salesorderoutstanding', 'display_purchaseorderoutstanding', 'display_reorderstatus'].includes(state.activeMenuLevel);

  const activeCompany = useStore(s => s.activeCompany);
  const globalMessage = useStore(s => s.globalMessage);

  const [isBooted, setIsBooted] = useState(false);
  const bootChecked = useRef(false);

  useEffect(() => {
    const checkCompanies = async () => {
      const count = await db.companies.count();
      if (!activeCompany && count === 0 && state.activeMenuLevel !== 'creation_company') {
        actions.handleItemSelect({ name: 'Create Company', level: 'creation_company' });
      }
      if (!bootChecked.current) {
        setIsBooted(true);
        bootChecked.current = true;
      }
    };
    checkCompanies();
  }, [actions, state.activeMenuLevel, activeCompany]);

  // Bridge: reports-home.html ke GSTR items ke liye React navStack navigation
  useEffect(() => {
    (window as any).navigateToGSTR = (level: string) => actions.pushMenu(level);
    return () => { delete (window as any).navigateToGSTR; };
  }, [actions]);

  if (!isBooted) {
    return <div className="h-screen w-full bg-[#f1f5f9] flex items-center justify-center font-bold text-[#1e8585]">Initializing Aarthika Pro...</div>;
  }

  return (
    <>
      <div className={`flex flex-col h-screen w-full bg-[#f1f5f9] font-sans overflow-hidden select-none ${printRequested ? 'no-print' : ''}`}>
        <LicensePanelModal />
      <TopHeader openMenu={state.openMenu} setOpenMenu={actions.setOpenMenu} onDropdownAction={actions.handleDropdownAction} config={APP_CONFIG} dropdownIndex={state.dropdownIndex} />
        
        <BreadcrumbBar navStack={derived.navStack} labels={APP_CONFIG.labels.breadcrumbs} />
        
        <div className="flex flex-1 overflow-hidden bg-[#e8ecec] relative">
          <div className="flex flex-1 w-full relative">
            {!activeCompany && !state.isCompanyCreationView && !state.isSelectCompanyView ? (
               <div className="flex flex-1 items-center justify-center bg-[#e8ecec]">
                 <div className="text-[#1e8585] font-bold">Please select or create a company to continue.</div>
               </div>
            ) : state.isLedgerCreationView ? (
               <LedgerCreationView 
                 config={APP_CONFIG}
                 handleItemSelect={actions.handleItemSelect}
                 ledgerId={state.selectedEntityId}
               />
            ) : state.isGroupCreationView ? (
               <GroupCreationView 
                 handleItemSelect={actions.handleItemSelect}
                 groupId={state.selectedEntityId}
               />
            ) : state.isCompanyCreationView ? (
               <CompanyCreationView 
                 handleItemSelect={actions.handleItemSelect} 
                 companyId={state.selectedEntityId}
               />
            ) : state.isSelectCompanyView ? (
               <SelectCompanyView 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
                 mode={state.activeMenuLevel === 'select_company' ? 'select' : (state.activeMenuLevel === 'alteration_company' ? 'alter' : (state.activeMenuLevel === 'delete_company' ? 'delete' : (state.activeMenuLevel === 'backup_company' ? 'backup' : 'select')))}
               />
            ) : state.activeMenuLevel === 'delete_company_confirm' ? (
               <CompanyDeleteConfirmView 
                 handleItemSelect={actions.handleItemSelect}
                 companyId={state.selectedEntityId}
               />
            ) : state.activeMenuLevel === 'backup_company_process' ? (
               <CompanyBackupView 
                 handleItemSelect={actions.handleItemSelect}
                 companyId={state.selectedEntityId}
               />
            ) : state.activeMenuLevel === 'restore_company' ? (
               <CompanyRestoreView
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.activeMenuLevel === 'customization' ? (
               <CustomizationView
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isDashboardView ? (
               <DashboardView />
            ) : state.isVoucherView ? (
               <VoucherEngineWrapper 
                 key={`voucher-wrapper-${state.activeMenuLevel}-${state.activeEntityId ?? 'new'}`}
                 handleItemSelect={actions.handleItemSelect}
                 passedEntityId={state.activeEntityId}
                 activeMenuLevel={state.activeMenuLevel}
               />
            ) : state.isDayBookView ? (
               <DayBookView 
                 config={APP_CONFIG} flatItems={derived.flatItems}
                 selectedIndex={state.selectedIndex} setSelectedIndex={actions.setSelectedIndex}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isBalanceSheetView ? (
               <BalanceSheetView 
                 config={APP_CONFIG} flatItems={derived.flatItems}
                 selectedIndex={state.selectedIndex} setSelectedIndex={actions.setSelectedIndex}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.activeMenuLevel === 'import_bank' ? (
               <div className="flex flex-col flex-1 w-full h-full overflow-hidden bg-[#e8ecec]"><BankImport /></div>
            ) : state.activeMenuLevel === 'import_invoice' ? (
               <div className="flex flex-col flex-1 w-full h-full overflow-hidden bg-[#e8ecec]"><InvoiceImport /></div>
            ) : state.isQuotationCreationView ? (
               <QuotationView handleItemSelect={actions.handleItemSelect} isAlterMode={false} />
            ) : state.isQuotationAlterView ? (
               <QuotationView
                 handleItemSelect={actions.handleItemSelect}
                 quotationId={useStore.getState().currentQuotationId}
                 isAlterMode={true}
               />
            ) : state.isQuotationListView ? (
               <QuotationListView handleItemSelect={actions.handleItemSelect} />
            ) : state.activeMenuLevel === 'display_groupsummary' ? (
               <ComingSoonView handleItemSelect={actions.handleItemSelect} title="Group Summary" />
            ) : state.activeMenuLevel === 'display_groupvouchers' ? (
               <ComingSoonView handleItemSelect={actions.handleItemSelect} title="Group Vouchers" />
            ) : state.activeMenuLevel === 'gstr1' ? (
               <div className="flex flex-col flex-1 w-full h-full overflow-hidden">
                 <Gstr1View onQuit={() => actions.pushMenu('main')} />
               </div>
            ) : state.activeMenuLevel === 'gstr3b' ? (
               <div className="flex flex-col flex-1 w-full h-full overflow-hidden">
                 <Gstr3bView onQuit={() => actions.pushMenu('main')} />
               </div>
            ) : state.activeMenuLevel === 'gst_summary' ? (
               <div className="flex flex-col flex-1 w-full h-full overflow-hidden">
                 <GstSummaryView onQuit={() => actions.pushMenu('main')} />
               </div>
            ) : state.activeMenuLevel === 'party_register' ? (
               <div className="flex flex-col flex-1 w-full h-full overflow-hidden">
                 <PartyRegisterView onQuit={() => actions.pushMenu('main')} />
               </div>
            ) : state.isProfitLossView ? (
               <ProfitLossView 
                 config={APP_CONFIG} flatItems={derived.flatItems}
                 selectedIndex={state.selectedIndex} setSelectedIndex={actions.setSelectedIndex}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isBinBoxView ? (
               <BinBoxView 
                 config={APP_CONFIG}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.activeMenuLevel === 'f10_center' ? (
               <F10CenterView 
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isLedgerVouchersView ? (
               <LedgerVouchersView 
                 config={APP_CONFIG} flatItems={derived.flatItems}
                 selectedIndex={state.selectedIndex} setSelectedIndex={actions.setSelectedIndex}
                 handleItemSelect={actions.handleItemSelect}
                 ledgerId={state.selectedEntityId}
               />
            ) : state.isGroupSummaryView ? (
               <GroupSummaryView 
                 config={APP_CONFIG} flatItems={derived.flatItems}
                 selectedIndex={state.selectedIndex} setSelectedIndex={actions.setSelectedIndex}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isCurrencyCreationView ? (
               <CurrencyCreationView handleItemSelect={actions.handleItemSelect} currencyId={state.selectedEntityId} />
            ) : state.isVoucherTypeCreationView ? (
               <VoucherTypeCreationView handleItemSelect={actions.handleItemSelect} voucherTypeId={state.selectedEntityId} />
            ) : ['display_outstanding', 'display_statistics', 'display_stockmovement', 'display_stockquery', 'display_costestimation', 'display_salesorderoutstanding', 'display_purchaseorderoutstanding', 'display_reorderstatus'].includes(state.activeMenuLevel) ? (
               <SimpleReportView 
                 activeMenuLevel={state.activeMenuLevel}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isTrialBalanceView ? (
               <TrialBalanceView 
                 config={APP_CONFIG} 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
               />
            ) : state.isCashFlowView ? (
                <CashFlowView 
                  config={APP_CONFIG} 
                  handleItemSelect={actions.handleItemSelect}
                  selectedIndex={state.selectedIndex}
                  setSelectedIndex={actions.setSelectedIndex}
                />
            ) : state.isFundsFlowView ? (
                <FundsFlowView 
                  config={APP_CONFIG} 
                  handleItemSelect={actions.handleItemSelect}
                  selectedIndex={state.selectedIndex}
                  setSelectedIndex={actions.setSelectedIndex}
                />
            ) : state.isRatioAnalysisView ? (
                <RatioAnalysisView
                  config={APP_CONFIG}
                  handleItemSelect={actions.handleItemSelect}
                  selectedIndex={state.selectedIndex}
                  setSelectedIndex={actions.setSelectedIndex}
                />
            ) : state.isBankReconciliationView ? (
                <BankReconciliationView
                  handleItemSelect={actions.handleItemSelect}
                />
            ) : state.isStockSummaryView ? (
               <StockSummaryView 
                 config={APP_CONFIG} 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
                 activeMenuLevel={state.activeMenuLevel}
               />
            ) : state.isStockItemVouchersView ? (
               <StockItemVouchersView 
                 config={APP_CONFIG} 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
                 stockItemId={state.selectedEntityId}
               />
            ) : state.isStockGroupCreationView ? (
               <StockGroupCreationView handleItemSelect={actions.handleItemSelect} stockGroupId={state.selectedEntityId} />
            ) : state.isStockCategoryCreationView ? (
               <StockCategoryCreationView handleItemSelect={actions.handleItemSelect} stockCategoryId={state.selectedEntityId} />
            ) : state.isStockItemCreationView ? (
               <StockItemCreationView handleItemSelect={actions.handleItemSelect} stockItemId={state.selectedEntityId} />
            ) : state.isUnitCreationView ? (
               <UnitCreationView handleItemSelect={actions.handleItemSelect} unitId={state.selectedEntityId} />
            ) : state.isLocationCreationView ? (
               <LocationCreationView handleItemSelect={actions.handleItemSelect} locationId={state.selectedEntityId} />
            ) : state.isRegisterSummaryView ? (
               <RegisterSummaryView 
                 config={APP_CONFIG} 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
                 activeMenuLevel={state.activeMenuLevel}
               />
            ) : state.isVoucherRegisterView ? (
               <VoucherRegisterView 
                 config={APP_CONFIG} 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
                 activeMenuLevel={state.activeMenuLevel}
                 selectedPeriod={state.selectedPeriod}
               />
            ) : state.isEmployeeCreationView ? (
               <EmployeeCreationView 
                 handleItemSelect={actions.handleItemSelect}
                 employeeId={state.selectedEntityId}
               />
            ) : state.isEmployeeListView ? (
               <EmployeeListView 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
               />
            ) : state.isPayrollProcessingView ? (
               <PayrollProcessingView 
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isPayrollSummaryView ? (
               <PayrollSummaryView 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
               />
            ) : state.activeMenuLevel === 'payroll_leave' ? (
                <LeaveManagementView handleItemSelect={actions.handleItemSelect} />
            ) : state.activeMenuLevel === 'payroll_attendance' ? (
                <AttendanceManagementView handleItemSelect={actions.handleItemSelect} />
            ) : state.activeMenuLevel === 'payroll_loans' ? (
                <LoanManagementView handleItemSelect={actions.handleItemSelect} />
            ) : state.isPayslipView ? (
               <PayslipView 
                 payrollRecord={state.selectedPayrollRecord}
                 handleItemSelect={actions.handleItemSelect}
               />
            ) : state.isSalaryComponentsView ? (
               <SalaryComponentListView 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
               />
             ) : state.isSalaryComponentCreationView ? (
               <SalaryComponentCreationView 
                 handleItemSelect={actions.handleItemSelect}
                 componentId={state.selectedEntityId}
               />
             ) : state.isSalaryStructureView ? (
               <SalaryStructureView 
                 handleItemSelect={actions.handleItemSelect}
                 selectedIndex={state.selectedIndex}
                 setSelectedIndex={actions.setSelectedIndex}
               />
             ) : state.isSalaryStructureEditView ? (
               <SalaryStructureEditView 
                 handleItemSelect={actions.handleItemSelect}
                 employeeId={state.selectedEntityId}
               />
             ) : ['payroll_salary_processing_detail', 'employees_add', 'employees_detail', 'payroll_reports_detail'].includes(state.activeMenuLevel) ? (
               <ComingSoonView 
                 handleItemSelect={actions.handleItemSelect}
                 title={APP_CONFIG.labels.breadcrumbs[state.activeMenuLevel] || 'Feature Upcoming'}
               />
             ) : state.isFullScreenList ? (
              <FullScreenListView 
                activeMenuLevel={state.activeMenuLevel} config={APP_CONFIG} flatItems={derived.flatItems}
                selectedIndex={state.selectedIndex} setSelectedIndex={actions.setSelectedIndex}
                handleItemSelect={actions.handleItemSelect} labels={APP_CONFIG.labels.breadcrumbs}
              />
            ) : (
              <>
                <CenterMenu
                  activeMenuLevel={state.centerMenuLevel} currentItemsData={derived.currentCenterItemsData}
                  selectedIndex={state.isListModal ? state.menuIndices[state.centerMenuLevel] : state.selectedIndex}
                  setSelectedIndex={actions.setSelectedIndex}
                  flatItems={state.isListModal ? derived.currentCenterItemsData.flatMap((g: any) => g.items) : derived.flatItems}
                  handleItemSelect={actions.handleItemSelect} masterSearch={state.isListModal ? '' : state.masterSearch}
                  setMasterSearch={actions.setMasterSearch} isDisabled={state.isListModal}
                  labels={APP_CONFIG.labels.breadcrumbs}
                />
                {state.isListModal && (
                  <ListModal
                     activeMenuLevel={state.activeMenuLevel} parentLevel={state.centerMenuLevel}
                     masterSearch={state.masterSearch} setMasterSearch={actions.setMasterSearch}
                     flatItems={derived.flatItems} selectedIndex={state.selectedIndex}
                     setSelectedIndex={actions.setSelectedIndex} handleItemSelect={actions.handleItemSelect}
                     labels={APP_CONFIG.labels.breadcrumbs}
                  />
                )}
                <LeftPanel />
              </>
            )}
          </div>
          {!hideSidebars && (
            <RightSidebar 
              isLocked={!activeCompany}
              config={APP_CONFIG} 
              onPeriodClick={() => {
                setTempFromDate(fromDate);
                setTempToDate(toDate);
                setShowPeriodModal(true);
              }}
              onShortcutClick={(btn: any) => {
                const e = { preventDefault: () => {}, stopPropagation: () => {} };
                // Simulate function key press logic
                if (btn.shortcut === 'F3') actions.handleItemSelect({ name: 'Select Company', level: 'select_company' });
                else if (btn.shortcut === 'B') actions.handleItemSelect({ name: 'Bin Box', level: 'bin_box' });
                else if (btn.shortcut === 'F10') actions.handleItemSelect({ name: 'F10 Center', level: 'f10_center' });
                else if (['F4','F5','F6','F7','F8','F9'].includes(btn.shortcut)) {
                   useStore.getState().setCurrentVoucherType(btn.label);
                   if (state.activeMenuLevel !== 'vouchers') actions.handleItemSelect({ name: 'Vouchers' });
                }
              }}
            />
          )}
        </div>
        {!hideSidebars && <StatusBar />}

        {showPeriodModal && (
          <div className="absolute inset-0 bg-black/20 z-[200] flex items-center justify-center period-modal">
            <div className="bg-white border border-black shadow-md w-[320px] flex flex-col p-6">
              <div className="text-center mb-6">
                <span className="font-bold text-[16px] border-b border-black pb-[1px]">Change Period</span>
              </div>
              
              <div className="flex flex-col gap-4 text-[15px] text-black">
                <div className="flex items-center">
                  <span className="w-[80px]">From</span>
                  <span className="mr-4">:</span>
                  <DateInput 
                    value={tempFromDate} 
                    onChange={(val) => setTempFromDate(val)}
                    className="flex-1 bg-[#eefcfc] border border-[#1e8585] px-2 py-[2px] font-bold text-center"
                    autoFocus
                    nextRef={toDateInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setShowPeriodModal(false);
                      }
                    }}
                  />
                </div>
                
                <div className="flex items-center">
                  <span className="w-[80px]">To</span>
                  <span className="mr-4">:</span>
                  <DateInput 
                    ref={toDateInputRef}
                    value={tempToDate} 
                    onChange={(val) => setTempToDate(val)}
                    className="flex-1 bg-[#eefcfc] border border-[#1e8585] px-2 py-[2px] font-bold text-center"
                    onConfirm={applyPeriod}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setShowPeriodModal(false);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {globalMessage && (
        <div className="absolute top-10 left-1/2 -translate-x-1/2 z-[200] bg-red-100 border border-red-500 text-red-700 px-6 py-2 rounded shadow-md font-bold text-sm animate-pulse whitespace-nowrap">
          {globalMessage}
        </div>
      )}

      <PrintManager activeMenuLevel={state.activeMenuLevel} selectedEntityId={state.selectedEntityId} />

      <QuickAccessSearch pushMenu={actions.pushMenu} />
      <KeyboardHelpOverlay />
    </>
  );
};